import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import NavigationHeader from '../homepage/components/NavigationHeader';
import MapLegend from './components/MapLegend';
import ThreatAlert from './components/ThreatAlert';
import LayerControl from './components/LayerControl';
import SearchLocation from './components/SearchLocation';
import WeatherOverlay from './components/WeatherOverlay';
import AnnotationPanel from './components/AnnotationPanel';
import ZoneInfoCard from './components/ZoneInfoCard';
import MapControls from './components/MapControls';

const InteractiveConservationMap = () => {
  const [legendOpen, setLegendOpen] = useState(true);
  const [layersOpen, setLayersOpen] = useState(false);
  const [annotationsOpen, setAnnotationsOpen] = useState(false);
  const [showWeather, setShowWeather] = useState(true);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [selectedZone, setSelectedZone] = useState(null);
  const [annotations, setAnnotations] = useState([]);
  
  const [alerts, setAlerts] = useState([
    {
      id: 1,
      severity: 'critical',
      title: 'Illegal Logging Detected',
      description: 'Unauthorized activity detected in Zone 3-A. Immediate ranger response required.',
      location: 'Zone 3-A, Sector North',
      time: '15 minutes ago'
    },
    {
      id: 2,
      severity: 'high',
      title: 'Weather Warning',
      description: 'Heavy rainfall expected in the next 2 hours. Visitor evacuation recommended for low-lying areas.',
      location: 'Multiple zones',
      time: '32 minutes ago'
    }
  ]);

  const [layers, setLayers] = useState([
    { id: 'biodiversity', name: 'Biodiversity Hotspots', description: 'Areas with high species concentration', enabled: true, opacity: 0.7 },
    { id: 'trails', name: 'Visitor Trails', description: 'Approved hiking and walking paths', enabled: true, opacity: 1 },
    { id: 'patrols', name: 'Ranger Patrol Routes', description: 'Active monitoring paths', enabled: true, opacity: 0.8 },
    { id: 'threats', name: 'Threat Indicators', description: 'Real-time threat detection zones', enabled: true, opacity: 0.9 },
    { id: 'weather', name: 'Weather Overlay', description: 'Precipitation and temperature data', enabled: showWeather, opacity: 0.6 },
    { id: 'research', name: 'Research Stations', description: 'Active field research locations', enabled: false, opacity: 1 },
    { id: 'water', name: 'Water Bodies', description: 'Rivers, lakes, and wetlands', enabled: true, opacity: 0.5 },
    { id: 'elevation', name: 'Elevation Contours', description: 'Topographic elevation lines', enabled: false, opacity: 0.4 }
  ]);

  const searchSuggestions = [
    { name: 'Mount Bloomfield Summit', type: 'Peak', coordinates: '9.7489° N, 118.5324° E', icon: 'Mountain' },
    { name: 'Tabon Cave Complex', type: 'Research Site', coordinates: '9.2683° N, 117.9731° E', icon: 'Microscope' },
    { name: 'Ranger Station Alpha', type: 'Facility', coordinates: '9.5124° N, 118.2456° E', icon: 'Shield' },
    { name: 'Biodiversity Zone 7', type: 'Protected Area', coordinates: '9.6234° N, 118.4123° E', icon: 'Sparkles' },
    { name: 'Visitor Trail 3', type: 'Trail', coordinates: '9.4567° N, 118.3789° E', icon: 'Route' }
  ];

  const weatherData = {
    temperature: 28,
    humidity: 82,
    windSpeed: 12,
    precipitation: 3.5,
    lastUpdated: '10 minutes ago'
  };

  const mockZones = [
    {
      id: 1,
      name: 'Core Conservation Zone',
      status: 'protected',
      area: 1250,
      description: 'Primary habitat for endangered Philippine cockatoo and other endemic species. Strict access control with research permits only.',
      biodiversityIndex: '9.2/10',
      threatLevel: 2,
      activeMonitoring: '24/7',
      rangerPatrols: 8,
      coordinates: { lat: 9.5234, lng: 118.3456 }
    },
    {
      id: 2,
      name: 'Buffer Zone North',
      status: 'moderate',
      area: 850,
      description: 'Transitional area with controlled visitor access. Active reforestation programs and wildlife corridor maintenance.',
      biodiversityIndex: '7.8/10',
      threatLevel: 5,
      activeMonitoring: 'Periodic',
      rangerPatrols: 4,
      coordinates: { lat: 9.6123, lng: 118.4234 }
    }
  ];

  const handleDismissAlert = (alertId) => {
    setAlerts(alerts?.filter(alert => alert?.id !== alertId));
  };

  const handleLayerToggle = (layerId, enabled, opacity) => {
    setLayers(layers?.map(layer => 
      layer?.id === layerId 
        ? { ...layer, enabled, ...(opacity !== undefined && { opacity }) }
        : layer
    ));
    
    if (layerId === 'weather') {
      setShowWeather(enabled);
    }
  };

  const handleSearch = (location) => {
    console.log('Searching for:', location);
  };

  const handleAddAnnotation = (annotation) => {
    setAnnotations([annotation, ...annotations]);
  };

  const handleZoneClick = (zoneId) => {
    const zone = mockZones?.find(z => z?.id === zoneId);
    setSelectedZone(zone);
  };

  const handleZoomIn = () => {
    console.log('Zoom in');
  };

  const handleZoomOut = () => {
    console.log('Zoom out');
  };

  const handleResetView = () => {
    console.log('Reset view');
    setSelectedZone(null);
  };

  const handleToggleFullscreen = () => {
    if (!document.fullscreenElement) {
      document.documentElement?.requestFullscreen();
      setIsFullscreen(true);
    } else {
      document.exitFullscreen();
      setIsFullscreen(false);
    }
  };

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  return (
    <div className="min-h-screen bg-background">
      {/* Navigation Header */}
      <NavigationHeader />
      
      {/* Add padding to account for fixed header */}
      <div className="pt-20">
        {/* Breadcrumb Navigation */}
        <div className="bg-muted/30 border-b border-border">
          <div className="container mx-auto px-4 py-3">
            <div className="flex items-center gap-2 text-sm">
              <Link to="/homepage" className="text-muted-foreground hover:text-foreground transition-colors">
                Home
              </Link>
              <Icon name="ChevronRight" size={14} color="var(--color-muted-foreground)" />
              <Link to="/command-center-dashboard" className="text-muted-foreground hover:text-foreground transition-colors">
                Command Center
              </Link>
              <Icon name="ChevronRight" size={14} color="var(--color-muted-foreground)" />
              <span className="text-foreground font-medium">Interactive Conservation Map</span>
            </div>
          </div>
        </div>

        {/* Map Container */}
        <div className="relative w-full h-[calc(100vh-220px)]">
          {/* Google Maps Iframe - Default pin at Puerto Princesa Subterranean River */}
          <iframe
            width="100%"
            height="100%"
            loading="lazy"
            title="Puerto Princesa Subterranean River National Park Conservation Map"
            referrerPolicy="no-referrer-when-downgrade"
            src="https://www.google.com/maps?q=10.1968,118.9213&z=14&output=embed"
            className="w-full h-full"
          />

          {/* Overlay Components */}
          <ThreatAlert alerts={alerts} onDismiss={handleDismissAlert} />
          <SearchLocation onSearch={handleSearch} suggestions={searchSuggestions} />
          <MapLegend isOpen={legendOpen} onToggle={() => setLegendOpen(!legendOpen)} />
          <WeatherOverlay weatherData={weatherData} isVisible={showWeather} />
          <LayerControl 
            layers={layers} 
            onLayerToggle={handleLayerToggle}
            isOpen={layersOpen}
            onToggle={() => setLayersOpen(!layersOpen)}
          />
          <AnnotationPanel
            isOpen={annotationsOpen}
            onToggle={() => setAnnotationsOpen(!annotationsOpen)}
            onAddAnnotation={handleAddAnnotation}
            annotations={annotations}
          />
          <ZoneInfoCard zone={selectedZone} onClose={() => setSelectedZone(null)} />
          <MapControls
            onZoomIn={handleZoomIn}
            onZoomOut={handleZoomOut}
            onResetView={handleResetView}
            onToggleFullscreen={handleToggleFullscreen}
            isFullscreen={isFullscreen}
          />

          {/* Quick Action Buttons */}
          <div className="absolute top-4 left-4 flex gap-2 z-[400]">
            <Button
              variant={showWeather ? 'default' : 'outline'}
              size="sm"
              iconName="CloudRain"
              onClick={() => {
                setShowWeather(!showWeather);
                handleLayerToggle('weather', !showWeather);
              }}
            >
              Weather
            </Button>
            <Button
              variant="outline"
              size="sm"
              iconName="Zap"
              onClick={() => handleZoneClick(1)}
            >
              Zones
            </Button>
          </div>

          {/* Status Bar */}
          <div className="absolute bottom-4 left-4 bg-card/95 backdrop-blur-sm rounded-lg shadow-conservation px-4 py-2 z-[400] flex items-center gap-6">
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 bg-success rounded-full pulse-conservation"></div>
              <span className="text-xs text-foreground">Live Monitoring Active</span>
            </div>
            <div className="text-xs text-muted-foreground">
              Last Update: {new Date()?.toLocaleTimeString()}
            </div>
            <div className="text-xs text-muted-foreground">
              Coordinates: 10.1968° N, 118.9213° E
            </div>
          </div>
        </div>

        {/* Footer */}
        <footer className="bg-card border-t border-border">
          <div className="container mx-auto px-4 py-6">
            <div className="flex flex-col md:flex-row items-center justify-between gap-4">
              <div className="text-sm text-muted-foreground">
                © {new Date()?.getFullYear()} Puerto Princesa Subterranean River National Park. UNESCO World Heritage Site.
              </div>
              <div className="flex items-center gap-6">
                <Link to="/emergency-alert-system" className="text-sm text-muted-foreground hover:text-foreground transition-colors flex items-center gap-2">
                  <Icon name="AlertTriangle" size={14} />
                  Emergency Protocols
                </Link>
                <Link to="/research-data-portal" className="text-sm text-muted-foreground hover:text-foreground transition-colors flex items-center gap-2">
                  <Icon name="Database" size={14} />
                  Data Portal
                </Link>
                <Link to="/community-engagement-platform" className="text-sm text-muted-foreground hover:text-foreground transition-colors flex items-center gap-2">
                  <Icon name="Users" size={14} />
                  Community
                </Link>
              </div>
            </div>
          </div>
        </footer>
      </div>
    </div>
  );
};

export default InteractiveConservationMap;